import axios from "axios";
import { useEffect, useState } from "react";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  getBasicFormatSections,
  getReportSectionNames,
  getReportSections,
  getUserName,
} from "../../../../common/EntitiesManagement";
import localize from "../../../../common/localize";
import ModalBackground from "../../../../components/common/ModalBackground";
import CheckboxComponent from "../../../../components/common/CheckboxComponent";
import Combobox from "../../../../components/common/Combobox";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import { encryptForUrl } from "../../../../common/utils";

export default function ReportSetup({
  locationHref,
  showGenerateInformModal,
  setShowGenerateInformModal,
  editionMode,
  title,
  reportSetupModel,
  setReportSetupModel,
  isReadOnly,
}) {
  const { REPORT_TYPE_ENDPOINT, config } = useGlobalContext();
  const reportSectionNames = getReportSectionNames();
  const [reportTypes, setReportTypes] = useState([]);
  const [reportSections, setReportSections] = useState(
    reportSetupModel?.ReportSections ?? getBasicFormatSections()
  );
  const [generateOriginal, setGenerateOriginal] = useState(false);
  const [printFormat, setPrintFormat] = useState({
    Id: 0,
    Name: localize.service_order,
  });

  const getReportTypes = async () => {
    const result = await axios.get(
      `${REPORT_TYPE_ENDPOINT}/${getUserName()}`,
      config
    );

    if (result.data?.Ok && result.data?.Data) {
      setReportTypes(result.data.Data);
    }
  };

  useEffect(() => {
    if (!editionMode) {
      getReportTypes();
    }
  }, []);

  const handleCheckedChanged = (e) => {
    switch (e.target.name) {
      case "rbtn_current_report":
        setGenerateOriginal(!e.target.checked);
        break;
      case "rbtn_original_report":
        setGenerateOriginal(e.target.checked);
        break;
      case "rbtn_normal_evidences":
        if (e.target.checked) {
          setReportSections({
            ...reportSections,
            panel_report: false,
          });
        }
        break;
      case "rbtn_panel_report":
        if (e.target.checked) {
          setReportSections({
            ...reportSections,
            panel_report: true,
          });
        }
        break;
      default:
        break;
    }
  };

  const handleSectionChange = (e) => {
    setReportSections({
      ...reportSections,
      [e.target.name]: e.target.checked,
    });

    if (editionMode) {
      setReportSetupModel({
        ...reportSetupModel,
        ReportSections: {
          ...reportSections,
          [e.target.name]: e.target.checked,
        },
      });
    }
  };

  const handleAllSectionsChange = (e) => {
    const newSections = Object.fromEntries(
      Object.entries(reportSections).map(([key, val]) => [
        key,
        typeof val === "boolean" ? e.target.checked : val,
      ])
    );

    setReportSections({
      ...newSections,
    });
  };

  const options = () => {
    const result = [
      {
        Id: 0,
        Name: localize.service_order,
        Default: true,
      },
      {
        Id: 1,
        Name: localize.report,
        Default: true,
      },
    ];

    if (reportTypes?.length > 0) {
      result.push(
        ...reportTypes.map((type) => ({
          Id: type.Id,
          Name: type.Name,
          Default: false,
        }))
      );
    }

    return result;
  };

  useEffect(() => {
    if (!editionMode) {
      switch (printFormat?.Id) {
        case 0:
          setReportSections(getBasicFormatSections());
          break;
        default:
          if (printFormat.Default) {
            setReportSections(getReportSections(printFormat?.Id));
          } else {
            const foundIndex = reportTypes.findIndex(
              (type) => type.Id === printFormat?.Id
            );

            if (foundIndex >= 0) {
              setReportSections(
                JSON.parse(reportTypes[foundIndex].ReportSections)
              );
            }
          }

          break;
      }
    }
  }, [printFormat]);

  const handlePrintFormatChange = (value) => {
    if (!value) {
      setPrintFormat({
        Id: 0,
        Name: localize.service_order,
        Default: true,
      });
      return;
    }

    setPrintFormat(value);
  };

  const areAllSectionsSelected = () => {
    return Object.entries(reportSections)
      .filter(([_, val]) => typeof val === "boolean")
      .every(([_, val]) => val === true);
  };

  const getEditionComponents = () => {
    return (
      <div className="w-full" style={{ marginTop: "10px" }}>
        <Box className="w-full flex items-center gap-2">
          <Box sx={{ width: "50%" }}>
            <label className="report_little_text">
              <b>{localize.report_sections}</b>
            </label>
          </Box>

          <Box sx={{ marginLeft: "5px" }}>
            <CheckboxComponent
              isReadOnly={isReadOnly}
              onValueChanged={handleAllSectionsChange}
              propertyName={"All"}
              text={localize.select_all}
              value={areAllSectionsSelected()}
            />
          </Box>
        </Box>

        <Divider />

        <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {reportSectionNames.map((section) => (
            <Grid item xs={6} key={Math.random()}>
              <CheckboxComponent
                isReadOnly={isReadOnly}
                onValueChanged={handleSectionChange}
                propertyName={section.name}
                text={section.title}
                value={reportSections[section.name]}
              />
            </Grid>
          ))}
        </Grid>
      </div>
    );
  };

  return editionMode ? (
    getEditionComponents()
  ) : (
    <ModalBackground
      isDrawerMode
      showModal={showGenerateInformModal}
      setShowModal={setShowGenerateInformModal}
      title={title ?? localize.print_service_order}
      okButtonText={localize.generate}
      onClickToSubmit={() => {
        setShowGenerateInformModal(false);
        const locationSplit = locationHref.split("?");
        window.open(
          `${
            locationSplit[0]
          }/report/${generateOriginal}?sections=${encryptForUrl(
            JSON.stringify(reportSections)
          )}`,
          "_blank"
        );
        setGenerateOriginal(false);
      }}
      onClickToCancel={() => {
        setShowGenerateInformModal(false);
        setGenerateOriginal(false);
      }}
      Component={
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
          className="mt-2"
        >
          {!editionMode && (
            <FormControl required fullWidth>
              <Combobox
                id="reportype"
                fieldLabel={localize.format}
                fieldOptions={options()}
                fieldValue={printFormat}
                handleValueChange={handlePrintFormatChange}
              />
            </FormControl>
          )}

          {(editionMode || printFormat?.Id > 0) && (
            <div style={{ paddingTop: "10px" }}>
              {!editionMode && (
                <Box className="flex flex-col gap-2">
                  <label className="report_little_text">
                    <b>{localize.report_type_and_evidences}</b>
                  </label>
                  <Divider />
                  <div className="flex justify-between">
                    <FormControl style={{ width: "50%" }}>
                      <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        defaultValue="false"
                        name="radio-buttons-group-report-type"
                      >
                        <FormControlLabel
                          name="rbtn_current_report"
                          value="false"
                          control={<Radio />}
                          label={
                            <span style={{ fontSize: "12px" }}>
                              {localize.generate_current_report_with_changes}
                            </span>
                          }
                          onChange={handleCheckedChanged}
                        />
                        <FormControlLabel
                          name="rbtn_original_report"
                          value="true"
                          control={<Radio />}
                          label={
                            <span style={{ fontSize: "12px" }}>
                              {localize.generate_original_report}
                            </span>
                          }
                          onChange={handleCheckedChanged}
                        />
                      </RadioGroup>
                    </FormControl>

                    <FormControl style={{ width: "50%" }}>
                      <RadioGroup
                        aria-labelledby="evidences-panel-report-radio-buttons-group-label"
                        defaultValue="true"
                        name="radio-buttons-group-evidences-panel-report"
                      >
                        <FormControlLabel
                          name="rbtn_panel_report"
                          value="true"
                          control={<Radio />}
                          label={
                            <span style={{ fontSize: "12px" }}>
                              {localize.panel_report}
                            </span>
                          }
                          onChange={handleCheckedChanged}
                        />

                        <FormControlLabel
                          name="rbtn_normal_evidences"
                          value="false"
                          control={<Radio />}
                          label={
                            <span style={{ fontSize: "12px" }}>
                              {localize.evidences_by_field}
                            </span>
                          }
                          onChange={handleCheckedChanged}
                        />
                      </RadioGroup>
                    </FormControl>
                  </div>
                </Box>
              )}

              {getEditionComponents()}
            </div>
          )}
        </div>
      }
    />
  );
}
